import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Shield, KeyRound, Trash2, SlidersHorizontal, Download } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { encryptData } from '../lib/encryption';
import { getSetting, setSetting, getApiKey, saveApiKey, deleteApiKey } from '../lib/db';
import { useTheme } from '../context/ThemeContext';

const PROVIDERS = [
    { id: 'openai', label: 'OpenAI' },
    { id: 'anthropic', label: 'Anthropic' },
    { id: 'gemini', label: 'Gemini' },
];

export const SettingsDialog = ({ open, onClose }) => {
    const [mode, setMode] = useState('managed'); // 'managed' | 'byok'
    const [provider, setProvider] = useState('openai');
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [maskedKey, setMaskedKey] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { theme, setTheme, darkIntensity, setDarkIntensity } = useTheme();

    useEffect(() => {
        if (!open) return;

        const loadSettings = async () => {
            try {
                setIsLoading(true);
                const [storedMode, storedProvider] = await Promise.all([
                    getSetting('aiMode'),
                    getSetting('aiProvider'),
                ]);

                const effectiveMode = storedMode || 'managed';
                const effectiveProvider = storedProvider || 'openai';

                setMode(effectiveMode);
                setProvider(effectiveProvider);

                const keyRecord = await getApiKey(effectiveProvider);
                if (keyRecord?.maskedKey) {
                    setMaskedKey(keyRecord.maskedKey);
                    setLastUpdated(keyRecord.savedAt || null);
                } else {
                    setMaskedKey('');
                    setLastUpdated(null);
                }
            } catch (error) {
                console.error('Failed to load AI settings', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, [open]);

    const handleProviderChange = async (value) => {
        setProvider(value);
        setApiKeyInput('');

        try {
            await setSetting('aiProvider', value);
            const keyRecord = await getApiKey(value);
            if (keyRecord?.maskedKey) {
                setMaskedKey(keyRecord.maskedKey);
                setLastUpdated(keyRecord.savedAt || null);
            } else {
                setMaskedKey('');
                setLastUpdated(null);
            }
        } catch (error) {
            console.error('Failed to update provider', error);
        }
    };

    const handleModeChange = async (value) => {
        setMode(value);
        try {
            await setSetting('aiMode', value);
        } catch (error) {
            console.error('Failed to update AI mode', error);
        }
    };

    const handleSaveKey = async () => {
        if (!apiKeyInput.trim()) {
            toast.error('Enter an API key to save');
            return;
        }

        try {
            setIsLoading(true);

            // Encrypt the key client-side with a fixed local password label.
            // This is only to protect at rest in IndexedDB, not against a fully compromised browser.
            const encryptedKey = await encryptData({ key: apiKeyInput.trim() }, 'local-api-key-protect');

            const visibleSuffix = apiKeyInput.trim().slice(-4) || '';
            const newMasked = `••••••••••${visibleSuffix ? ' ' + visibleSuffix : ''}`;

            await saveApiKey(provider, encryptedKey, newMasked);
            await setSetting('aiMode', 'byok');

            setMode('byok');
            setApiKeyInput('');
            setMaskedKey(newMasked);
            setLastUpdated(new Date().toISOString());

            toast.success('API key saved securely in your browser');
        } catch (error) {
            console.error('Failed to save API key', error);
            toast.error('Failed to save API key');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearKey = async () => {
        try {
            setIsLoading(true);
            await deleteApiKey(provider);
            setMaskedKey('');
            setLastUpdated(null);
            if (mode === 'byok') {
                await setSetting('aiMode', 'managed');
                setMode('managed');
            }
            toast.success('API key removed from this browser');
        } catch (error) {
            console.error('Failed to delete API key', error);
            toast.error('Failed to delete API key');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setApiKeyInput('');
        onClose();
    };

    const formatLastUpdated = (value) => {
        if (!value) return null;
        try {
            const d = new Date(value);
            return d.toLocaleString();
        } catch {
            return null;
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md rounded-2xl border-slate-200/60" data-testid="settings-dialog">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-light text-slate-800">
                        Settings
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Tune how IdeaVault feels and how AI participates.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Appearance */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-medium text-slate-800 flex items-center gap-2">
                            <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                            Appearance
                        </h3>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-slate-600">Theme</p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 rounded-full px-3 text-xs"
                                    onClick={() => (theme === 'dark' ? setTheme('light') : setTheme('dark'))}
                                    disabled={isLoading}
                                >
                                    {theme === 'dark' ? 'Dark' : 'Light'} mode
                                </Button>
                            </div>

                            {theme === 'dark' && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-slate-600">Dark mode intensity</Label>
                                    <input
                                        type="range"
                                        min="0.8"
                                        max="1.2"
                                        step="0.05"
                                        value={darkIntensity}
                                        onChange={(e) => {
                                            const value = Number(e.target.value);
                                            const clamped = Math.min(1.2, Math.max(0.8, value));
                                            setDarkIntensity(clamped);
                                        }}
                                        className="w-full accent-slate-700"
                                    />
                                    <p className="text-[11px] text-slate-500">
                                        Adjusts how deep the dark background feels. Text and accents stay the same.
                                    </p>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* AI */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-medium text-slate-800 flex items-center gap-2">
                            <Shield className="h-4 w-4 text-emerald-600" />
                            AI
                        </h3>
                        <div className="space-y-3">
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 space-y-2">
                                <p className="text-xs font-medium text-emerald-800">Managed AI (default)</p>
                                <p className="text-xs text-emerald-700 leading-relaxed">
                                    Uses our managed key. Your ideas stay local; only the text you send to tools is processed.
                                </p>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={mode === 'managed' ? 'default' : 'outline'}
                                    className="h-8 rounded-full px-3 text-xs mt-1.5"
                                    onClick={() => handleModeChange('managed')}
                                    disabled={isLoading}
                                >
                                    Use managed AI
                                </Button>
                            </div>

                            <details className="rounded-xl border border-slate-200 bg-slate-50/80 p-4" open={false}>
                                <summary className="flex items-center justify-between gap-2 cursor-pointer list-none">
                                    <div className="flex items-center gap-2">
                                        <KeyRound className="h-4 w-4 text-slate-500" />
                                        <span className="text-xs font-medium text-slate-700">Bring Your Own Key</span>
                                    </div>
                                    <span className="text-[11px] text-slate-500">
                                        {maskedKey ? 'Key saved' : 'No key yet'}
                                    </span>
                                </summary>
                                <div className="mt-3 space-y-3">
                                    <p className="text-[11px] text-slate-500">
                                        Your API key is encrypted and stored only in this browser. It is never logged or stored on our servers.
                                    </p>

                                    <div className="space-y-2">
                                        <Label className="text-xs text-slate-600">Provider</Label>
                                        <Select value={provider} onValueChange={handleProviderChange}>
                                            <SelectTrigger className="h-9 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {PROVIDERS.map((p) => (
                                                    <SelectItem key={p.id} value={p.id} className="text-xs">
                                                        {p.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-1">
                                        <Label className="text-xs text-slate-600">API Key</Label>
                                        <Input
                                            type="password"
                                            value={apiKeyInput}
                                            onChange={(e) => setApiKeyInput(e.target.value)}
                                            placeholder={`Paste your ${provider} API key`}
                                            className="h-9 text-xs"
                                        />
                                        <div className="flex items-center justify-between mt-1">
                                            <div className="text-[11px] text-slate-500">
                                                {maskedKey ? (
                                                    <span>Saved: {maskedKey}</span>
                                                ) : (
                                                    <span>No key saved yet for this provider.</span>
                                                )}
                                                {lastUpdated && (
                                                    <span className="ml-2 opacity-80">
                                                        · Updated {formatLastUpdated(lastUpdated)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {maskedKey && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-500 hover:text-slate-700"
                                                        onClick={handleClearKey}
                                                        disabled={isLoading}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    className="h-8 rounded-full px-3 text-xs"
                                                    onClick={handleSaveKey}
                                                    disabled={isLoading}
                                                >
                                                    Save key
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={mode === 'byok' ? 'default' : 'outline'}
                                        className="h-8 rounded-full px-3 text-xs mt-2"
                                        onClick={() => handleModeChange('byok')}
                                        disabled={isLoading}
                                    >
                                        Use my key for AI
                                    </Button>
                                </div>
                            </details>
                        </div>
                    </section>

                    {/* Data */}
                    <section className="space-y-2">
                        <h3 className="text-sm font-medium text-slate-800 flex items-center gap-2">
                            <Download className="h-4 w-4 text-slate-500" />
                            Data
                        </h3>
                        <p className="text-xs text-slate-500">
                            Export and back up your ideas from the main app.
                        </p>
                    </section>

                    <p className="text-xs text-slate-400 text-center px-4">
                        All your ideas are stored locally in your browser.
                        Only AI tool content passes through our servers.
                    </p>
                </div>

                <DialogFooter>
                    <Button
                        onClick={handleClose}
                        className="rounded-full w-full bg-slate-900 hover:bg-slate-800 h-11"
                        data-testid="close-settings-btn"
                        disabled={isLoading}
                    >
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
